import React from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import { Toaster } from 'react-hot-toast'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

import App from './App'
import { store } from './store'
import { Material3ThemeProvider } from './contexts/ThemeContext'
import { registerServiceWorker, setupInstallPrompt } from './utils/pwaUtils'
import './index.css'

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// Register service worker for PWA (enabled in both dev and prod)
registerServiceWorker()
  .then((registration) => {
    if (registration) {
      console.log('[PWA] Service worker registered and ready')
    }
  })
  .catch((error) => {
    console.error('[PWA] Service worker registration failed:', error)
  })

// Setup PWA install prompt
setupInstallPrompt()

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Material3ThemeProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <App />
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: 'var(--md3-surface-container-high)',
                    color: 'var(--md3-on-surface)',
                    boxShadow: 'var(--md3-elevation-3)',
                    borderRadius: 'var(--md3-shape-corner-medium)',
                    border: 'none',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  },
                  success: {
                    duration: 3000,
                    style: {
                      background: 'var(--md3-success-container)',
                      color: 'var(--md3-on-success-container)',
                      boxShadow: 'var(--md3-elevation-2)',
                    },
                    iconTheme: {
                      primary: 'var(--md3-success)',
                      secondary: 'var(--md3-on-success)',
                    },
                  },
                  error: {
                    duration: 5000,
                    style: {
                      background: 'var(--md3-error-container)',
                      color: 'var(--md3-on-error-container)',
                      boxShadow: 'var(--md3-elevation-2)',
                    },
                    iconTheme: {
                      primary: 'var(--md3-error)',
                      secondary: 'var(--md3-on-error)',
                    },
                  },
                  loading: {
                    style: {
                      background: 'var(--md3-primary-container)',
                      color: 'var(--md3-on-primary-container)',
                      boxShadow: 'var(--md3-elevation-2)',
                    },
                    iconTheme: {
                      primary: 'var(--md3-primary)',
                      secondary: 'var(--md3-on-primary)',
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Material3ThemeProvider>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Provider>
  </React.StrictMode>,
)
